import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["use", "inputRef", "styled", "error", "resize", "value", "cols", "rows", "onChange", "onBlur", "onFocus", "onKeyDown", "onKeyUp", "onKeyPress", "disabled", "autoFocus", "placeholder", "readOnly", "disableFocusStyles"];
import { useContext, forwardRef, memo } from 'react';
import ShareInput from '../ShareInput/ShareInput';
import { FieldsetContext } from '../../context/FieldsetContext';
import styled from 'foundations-theming/utils';
import { setInputAppearance } from './TextArea.helpers';
import { setUiTransition } from '../../utils/styles/styles';
import { filterProps } from '../../../utils/filterProps';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useMatchTextAreaContext } from '../MatchTextArea/MatchTextAreaContext';
import { useExpandingTextAreaContext } from '../ExpandingTextArea/ExpandingTextAreaContext';
import { useCopyTextAreaContext } from '../CopyTextArea/CopyTextAreaContext';
import { useIsDisplayOptimized } from '../../context/DisplayOptimizedContext';
import { useComputedPlaceholder } from '../../../hooks/usePlaceholder';
import { Controllable } from '../Controllable';
import { HEIGHT } from '../../../constants/nonTokenisedValues';
import { CSS_SPECIFICITY } from '../../constants/CSSSpecificity';
import { getFocusStyleObject } from '../../utils/focus/focus.style';
import { useDisableFocusStyles } from '../../../hooks/useDisableFocusStyles';
import { jsx as _jsx } from "react/jsx-runtime";
export const StyledTextArea = styled.textarea.withConfig({
  displayName: "TextArea__StyledTextArea"
})(({
  $isCode,
  $unstyled,
  $use,
  $error,
  rows,
  $resize,
  $readOnly,
  cols,
  $fieldsize,
  $isBgTransparent,
  $matchTextAreaSize,
  theme,
  $height,
  $maxHeight,
  $overflow,
  $cssStyles,
  $isDisplayOptimized,
  $disableFocusStyles
}) => {
  const $hasMinimalStyling = $isDisplayOptimized || $unstyled;
  return Object.assign({
    color: $isCode ? theme.color['text-core-default'] : theme.color['icon-core-default']
  }, setInputAppearance(theme), $isCode && {
    fontFamily: theme.typography['code-100'].fontFamily,
    fontWeight: theme.typography['code-100'].fontWeight,
    backgroundColor: theme.color['fill-tertiary-disabled']
  }, {
    paddingInline: $isDisplayOptimized ? theme.space['0'] : theme.space['400'],
    paddingBlock: theme.space['200']
  }, setUiTransition(), {
    height: 'auto'
  }, ($hasMinimalStyling || $isBgTransparent) && !$isCode ? {
    backgroundColor: `${theme.color['fill-transparent-default']} !important`
  } : {}, $use === 'on-dark' && !$error && !$hasMinimalStyling && {
    backgroundColor: theme.color['fill-field-default-alt']
  }, $error ? {
    borderColor: `${theme.color['border-alert-default']} !important`
  } : {}, $isCode ? {
    borderColor: `${theme.color['border-core-default']} !important`
  } : {}, $hasMinimalStyling && {
    borderColor: `${theme.color['fill-transparent-default']} !important`,
    boxShadow: 'none !important'
  }, $isDisplayOptimized && {
    paddingBlock: theme.space['100'],
    border: 'none',
    fontSize: 'var(--trellis-typography-body-100-font-size)',
    lineHeight: '22px'
  }, $matchTextAreaSize === 'md' && {
    fontSize: theme.typography['body-200'].fontSize
  }, $matchTextAreaSize === 'xxl' && {
    fontSize: '32px',
    lineHeight: '40px'
  }, {
    minHeight: $isCode ? '155px' : rows || $fieldsize === 'small' || $isDisplayOptimized ? '0' : theme.space['1000'],
    resize: !$resize ? 'none' : $resize === 'horizontal' ? 'horizontal' : $resize === 'vertical' ? 'vertical' : undefined
  }, !$resize && {
    display: 'inline-block'
  }, cols && cols > 0 && {
    width: 'auto'
  }, $fieldsize === 'small' && Object.assign({}, theme.typography['body-100'], {
    height: HEIGHT.TEXT_AREA_FIELDSET_SMALL,
    paddingBlock: theme.space['100']
  }), getFocusStyleObject({
    theme,
    disableFocusStyles: $disableFocusStyles
  }), {
    '&:disabled': {
      backgroundColor: theme.color['fill-field-disabled'],
      borderColor: theme.color['border-core-disabled'],
      boxShadow: 'none !important',
      color: theme.color['border-core-default'],
      cursor: 'not-allowed'
    }
  }, $readOnly && {
    '&:not([aria-expanded="true"])': {
      backgroundColor: theme.color['fill-field-disabled'],
      borderColor: theme.color['border-core-disabled'],
      boxShadow: 'none'
    }
  }, {
    '&::-moz-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      opacity: 1,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    },
    '&:-ms-input-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    },
    '&::-webkit-input-placeholder': {
      fontWeight: theme.typography['body-300'].fontWeight,
      color: $hasMinimalStyling && $use === 'on-dark' ? theme.color['text-core-subtle'] : $use === 'on-dark' ? theme.color['text-core-subtle'] : theme.color['text-core-subtle']
    }
  }, $unstyled && {
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    fontSize: 'inherit',
    lineHeight: 'inherit',
    letterSpacing: 'inherit'
  }, {
    // styles calculated at runtime by `useExpandingTextAreaContext` for
    // expanding text areas. specificity is increased so that callers won't
    // override them accidentally, otherwise expanding text areas won't expand.
    // See https://git.hubteam.com/HubSpot/foundations-components/issues/5729
    [CSS_SPECIFICITY.OVERRIDE]: Object.assign({}, $height && {
      height: $height
    }, $maxHeight && {
      maxHeight: $maxHeight
    }, $overflow && {
      overflow: $overflow
    })
  }, $cssStyles);
});
export const TextAreaComponent = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      use,
      inputRef,
      styled: styledProp = true,
      error = false,
      resize = 'vertical',
      value = '',
      cols,
      rows,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      onKeyUp,
      onKeyPress,
      disabled,
      autoFocus,
      placeholder,
      readOnly,
      disableFocusStyles: disableFocusStylesProp
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const context = useContext(FieldsetContext);
  const matchTextAreaContext = useMatchTextAreaContext();
  const expandingTextAreaContext = useExpandingTextAreaContext();
  const copyTextAreaContext = useCopyTextAreaContext();
  const isDisplayOptimized = useIsDisplayOptimized();
  const {
    size: fieldSize,
    disabled: fieldDisabled
  } = context;
  const disableFocusStyles = useDisableFocusStyles(disableFocusStylesProp);
  const {
    domProps,
    tokenProps
  } = filterProps(rest, {
    isTextArea: true
  });
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const computedDisabled = disabled || fieldDisabled;
  const computedPlaceholder = useComputedPlaceholder(placeholder);
  return /*#__PURE__*/_jsx(StyledTextArea, Object.assign({
    $cssStyles: cssStyles,
    $error: error,
    $fieldsize: fieldSize,
    $height: (expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.height) || (matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.height),
    $isBgTransparent: (matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.isBgTransparent) || (copyTextAreaContext === null || copyTextAreaContext === void 0 ? void 0 : copyTextAreaContext.isBgTransparent),
    $isCode: copyTextAreaContext === null || copyTextAreaContext === void 0 ? void 0 : copyTextAreaContext.isCode,
    $isDisplayOptimized: isDisplayOptimized,
    $maxHeight: expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.maxHeight,
    $matchTextAreaSize: matchTextAreaContext === null || matchTextAreaContext === void 0 ? void 0 : matchTextAreaContext.size,
    $overflow: expandingTextAreaContext === null || expandingTextAreaContext === void 0 ? void 0 : expandingTextAreaContext.overflow,
    $resize: resize,
    $unstyled: !styledProp,
    $use: use,
    cols: cols,
    rows: rows
    // TODO: remove cast when on React 19 types (HubSpotEngineering/fefw#73)
    ,
    ref: inputRef || ref,
    value: value,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    onKeyPress: onKeyPress,
    disabled: computedDisabled,
    autoFocus: autoFocus,
    $readOnly: readOnly,
    readOnly: readOnly,
    placeholder: computedPlaceholder,
    $disableFocusStyles: disableFocusStyles
  }, domProps));
});
const defaultProps = {
  value: ''
};
export const TextArea = ShareInput(Controllable( /*#__PURE__*/memo(TextAreaComponent), ['value'], defaultProps));
TextArea.displayName = 'TextArea';